// services/errorHandlingService.ts
export enum ErrorType {
  NotFound = 'NOT_FOUND',
  Unauthorized = 'UNAUTHORIZED',
  Forbidden = 'FORBIDDEN',
  BadRequest = 'BAD_REQUEST',
  ServerError = 'SERVER_ERROR',
  NetworkError = 'NETWORK_ERROR',
  Timeout = 'TIMEOUT',
  Unknown = 'UNKNOWN',
}

/**
 * Thrown when the server answered 404.
 *
 * The interceptor in boot/axios.js raises this for every 404, so callers get a
 * real error to catch instead of a response of `undefined`. Stores may also
 * throw it themselves when they decide a missing record counts as not found.
 */
export class NotFoundError extends Error {
  /**
   * The error this was built from, when there was one - normally the axios
   * error, which still carries the status, config and response. Kept for
   * debugging; nothing user-facing should read it.
   */
  readonly originalError?: unknown;

  constructor(message: string, originalError?: unknown) {
    super(message);
    this.name = 'NotFoundError';
    this.originalError = originalError;
  }
}

export interface ApiError {
  type: ErrorType;
  message: string;
  statusCode?: number;
  originalError?: unknown;
}

/** The parts of a rejected request we read, without depending on axios types. */
type MaybeAxiosError = {
  code?: string;
  message?: string;
  request?: unknown;
  response?: { status: number; data?: { message?: string } };
};

export class ErrorHandlingService {
  parseApiError(error: unknown): ApiError {
    if (error instanceof NotFoundError) {
      return {
        type: ErrorType.NotFound,
        message: 'The requested resource was not found',
        originalError: error,
      };
    }

    const err = (error ?? {}) as MaybeAxiosError;

    // Check the timeout codes before the network branch below: a timed out
    // request also has a request and no response, so it would otherwise be
    // reported as a connection problem.
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
      return {
        type: ErrorType.Timeout,
        message: 'The request timed out. Please try again.',
        originalError: error,
      };
    }

    // Handle axios errors
    if (err.response) {
      const { status } = err.response;

      switch (status) {
        case 404:
          return {
            type: ErrorType.NotFound,
            message: 'The requested resource was not found',
            statusCode: status,
            originalError: error,
          };
        case 401:
          return {
            type: ErrorType.Unauthorized,
            message: 'Authentication required',
            statusCode: status,
            originalError: error,
          };
        case 403:
          return {
            type: ErrorType.Forbidden,
            message: 'You do not have permission to access this resource',
            statusCode: status,
            originalError: error,
          };
        case 400:
          return {
            type: ErrorType.BadRequest,
            message: err.response.data?.message || 'Invalid request',
            statusCode: status,
            originalError: error,
          };
        default:
          if (status >= 500) {
            return {
              type: ErrorType.ServerError,
              message: 'Server error occurred. Please try again later.',
              statusCode: status,
              originalError: error,
            };
          }
      }
    }

    // Network errors
    if (err.request && !err.response) {
      return {
        type: ErrorType.NetworkError,
        message: 'Network error. Please check your connection.',
        originalError: error,
      };
    }

    // Unknown errors
    return {
      type: ErrorType.Unknown,
      message: err.message || 'An unexpected error occurred',
      originalError: error,
    };
  }
}

export const errorHandlingService = new ErrorHandlingService();
